import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { StatCard } from 'core/elements';
import { IVendorQuoteImportSummary } from 'dashboard/types';
import { Color } from 'theme/enums';

import { formatDurationSeconds } from './utils';

// Measured, not guessed: the widest thing a card must hold on one line is the
// poll/push line at 120px, plus the compact card's 12px padding either side.
// Seven of these plus their gaps come to ~1110px, so they stay on one line on a
// laptop; above that the label and value share a line and the strip gets
// shorter still.
const CARD_MIN_WIDTH = 145;

interface IProps {
  summary?: IVendorQuoteImportSummary;
  isLoading: boolean;
}

export const StatCards = ({ summary, isLoading }: IProps) => {
  const { t } = useTranslation();

  const anomalyCount = summary?.anomalyCount ?? 0;

  const stats: {
    label: string;
    value: number | string;
    secondaryValue?: string;
    valueColor?: Color;
  }[] = [
    {
      label: t('labelTotalUploads'),
      value: summary?.totalUploads ?? 0,
      secondaryValue: t('labelViaPollPush', {
        poll: summary?.deliveredViaPoll ?? 0,
        push: summary?.deliveredViaPush ?? 0,
      }),
    },
    { label: t('labelSuccessful'), value: summary?.successful ?? 0 },
    { label: t('labelFailed'), value: summary?.failed ?? 0 },
    { label: t('labelInProgress'), value: summary?.inProgress ?? 0 },
    { label: t('labelUniqueUsers'), value: summary?.uniqueUsers ?? 0 },
    {
      label: t('labelAvgTimeToResult'),
      value: formatDurationSeconds(summary?.avgDurationSeconds),
    },
    {
      label: t('labelAnomalies'),
      value: anomalyCount,
      // Warning styling only when anomalies exist; a zero stays neutral.
      valueColor: anomalyCount > 0 ? Color.WARNING : undefined,
    },
  ];

  return (
    // auto-fit rather than a fixed 12-column split: seven cards never divide
    // evenly into twelve, so the old lg={2} always pushed the seventh onto a
    // second row and cost the table that height. This fits as many as the width
    // allows and stretches them to fill it, never narrower than one card needs.
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: `repeat(auto-fit, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
      }}
    >
      {stats.map((stat) => (
        <StatCard
          key={stat.label}
          isLoading={isLoading}
          isCompact
          label={stat.label}
          value={stat.value}
          secondaryValue={stat.secondaryValue}
          valueColor={stat.valueColor}
        />
      ))}
    </Box>
  );
};
